import React, { useState, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import DataOutputProcessed from "./DataOutputProcessed";
import DataOutputOriginal from "./DataOutputOriginal";
import FieldMappings from "./FieldMappings";
import NoticeStack, { useNoticeStack } from "../../NoticeStack";
import TabBar from "./TabBar";
import DataInput from "../DataInput";
import { DragDropContext } from "react-beautiful-dnd";
import Http from "./SubmissionHttp";
import useProcessing from "../hooks/useProcessing";
import DataSubmissionHeader from "../DataSubmissionHeader";
import SubmissionSummary from "./SubmissionSummary";
import dataImage from "~/images/submission_empty_state.png";
import ProcessingFileModal from "../ProcessingFileModal";
import LeavePageModal from "../LeavePageModal";
import SummarySideBar from "./SummarySideBar";
import { MappingWarningIcon, MappingErrorIcon } from "./MappingIcons";
import { showAmgenWarning } from "./AmgenNotice";

const FILENAME_PREFIXES = {
  pharmacy: "340B_ESP_Pharmacy",
  medical: "340B_ESP_Medical",
  pharmacy_encounter: "340B_ESP_Pharmacy_Encounter",
  medical_encounter: "340B_ESP_Medical_Encounter",
};

const DataSubmission = (props) => {
  const [submissions, setSubmissions] = useState([]);
  const [salt, setSalt] = useState();
  const { notices, addNotice, dismissNotice } = useNoticeStack();
  const [activeTab, setActiveTab] = useState("submission_summary");
  const [viewFileRequest, setViewFileRequest] = useState(null);
  const [leavePageLink, setLeavePageLink] = useState(null);

  const filenamePrefix = FILENAME_PREFIXES[props.submissionType] || "340B_ESP";
  const [submissionName, setSubmissionName] = useState(
    `${filenamePrefix}_${moment().format("YYYY-MM-DD_HH-mm-ss")}`
  );

  const [fieldMappingGroups, setFieldMappingGroups] = useState(props.fieldMapping);
  const [creatingMapping, setCreatingMapping] = useState(false);

  const onMappingError = () =>
    addNotice({ kind: "error", message: "Oh no - it looks like something went wrong" });

  // Drag and Drop context requires drop handler to be defined in DataSubmissions
  // The actively editing MappingGroup writes its local handler in this ref, and onDragEnd dispatches drops into it.
  const dropFnRef = useRef(null);

  const requiredFields = props.fieldMappingFields.filter((f) => f.required).map((f) => f.name);
  const ndcList = useMemo(() => JSON.parse(props.ndcList), [props.ndcList]);
  const accessibleEntityIds = useMemo(
    () => props.accessibleEntityIds ?? [],
    [props.accessibleEntityIds]
  );
  useEffect(() => {
    fetchVault();
  }, []);

  const fetchVault = () => {
    if (salt && salt.length === 64) return;

    Http.getSalt(props.organization)
      .then((res) => setSalt(res.value))
      .catch(() =>
        addNotice({
          kind: "error",
          message: "Oh no - it looks like something went wrong",
        })
      );
  };

  const warnOnManufacturerErrors = (attachments) => {
    if (!attachments.some((a) => a.hasErrors)) return;

    addNotice({
      kind: "warning",
      message: `Validation alerts detected in your file.`,
    });
  };

  const warnOnColumnMappingIssues = (attachments) => {
    if (!attachments.some((a) => a.hasColumnMappingIssues)) return;

    const errorFilenames = attachments.filter((a) => a.hasMappingErrors).map((a) => a.filename);

    if (errorFilenames.length > 0) {
      addNotice({
        kind: "error",
        message: `${errorFilenames.join(", ")}: Invalid or incorrect column mapping.`,
      });
    }

    if (attachments.some((a) => a.hasMappingWarnings)) {
      addNotice({
        kind: "warning",
        message: "Column mapping warnings found. Check the Submission Data tab.",
      });
    }
  };

  // useProcessing creates a new submission object on mapping updates, so this either
  // appends a new submission, or replaces a newly updated submission and re-renders
  const placeAttachment = (attachment) =>
    setSubmissions((prev) => {
      const index = prev.findIndex((s) => s.fileId === attachment.fileId);
      if (index === -1) return [...prev, attachment];
      const submissionsListCopy = [...prev];
      submissionsListCopy[index] = attachment;
      return submissionsListCopy;
    });

  const {
    processingModalOpen,
    processingProgress,
    handleFile,
    handleSelectMapping,
    reprocessAffectedFiles,
  } = useProcessing({
    kind: props.submissionType,
    ndcList,
    accessibleEntityIds,
    salt,
    fieldMappingGroups,
    onProcessed: (attachments) => {
      attachments.forEach((a) => {
        placeAttachment(a);
      });
      warnOnManufacturerErrors(attachments);
      warnOnColumnMappingIssues(attachments);
    },
    onError: (message) => addNotice({ kind: "error", message }),
    onReprocessError: (message) => addNotice({ kind: "error", message }),
  });

  const createMapping = (name, fields) =>
    Http.createFieldMapping(props.mappingPath, name, fields, props.submissionType)
      .then((mapping) => {
        setFieldMappingGroups((prev) => [...prev, mapping]);
        setCreatingMapping(false);
        return mapping;
      })
      .catch((err) => {
        onMappingError();
        return Promise.reject(err);
      });

  const updateMapping = (id, changes) =>
    Http.updateFieldMapping(props.mappingPath, id, changes, props.submissionType)
      .then((mapping) => {
        setFieldMappingGroups((prev) => prev.map((m) => (m.id === id ? mapping : m)));
        reprocessAffectedFiles(submissions, mapping);
        return mapping;
      })
      .catch((err) => {
        onMappingError();
        return Promise.reject(err);
      });

  const deleteMapping = (id) =>
    Http.deleteMappingGroup(props.mappingPath, id)
      .then((res) => setFieldMappingGroups(res.mappings))
      .catch((err) => {
        onMappingError();
        return Promise.reject(err);
      });

  const inUseMappingIds = useMemo(() => {
    return submissions.filter((s) => s.mapping?.id).map((s) => s.mapping.id);
  }, [submissions]);

  const tabs = useMemo(
    () => [
      { id: "submission_summary", label: "Submission Summary" },
      {
        id: "submissions_data",
        label: "Submission Data",
        icon: submissions.some((s) => s.hasErrors) && <MappingWarningIcon />,
      },
      { id: "original_files", label: "Original Files" },
      {
        id: "column_mappings",
        label: "Column Mappings",
        icon: submissions.some((s) => s.hasMappingErrors) && <MappingErrorIcon />,
      },
    ],
    [submissions, props.submissionType]
  );

  const reset = () => {
    setSubmissions([]);
  };

  const removeSelectedFile = (fileId) => {
    setSubmissions((prev) => prev.filter((s) => s.fileId !== fileId));
    setViewFileRequest({ fileId: "__all__", isDeletion: true });
  };

  const handleViewFile = (fileId) => {
    setViewFileRequest({ fileId });
    setActiveTab("submissions_data");
  };

  const mappingView = () => {
    return (
      <div className="tabs__view" style={{ display: "block" }}>
        <FieldMappings
          organization={props.organization}
          createMapping={createMapping}
          updateMapping={updateMapping}
          deleteMapping={deleteMapping}
          creatingMapping={creatingMapping}
          setCreatingMapping={setCreatingMapping}
          fieldMappingFields={props.fieldMappingFields}
          mappings={fieldMappingGroups}
          mappingType={props.submissionType}
          inUseMappingIds={inUseMappingIds}
          dropFnRef={dropFnRef}
        />
      </div>
    );
  };

  const renderTabViews = () => {
    if (activeTab === "column_mappings") return mappingView();

    if (!submissions.length) {
      return (
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "50vh", minHeight: "fit-content" }}
        >
          <img src={dataImage} height={240} width={280} alt="No files attached" />
        </div>
      );
    }

    return (
      <>
        <div style={{ display: activeTab === "submission_summary" ? "block" : "none" }}>
          <div className="flex gap--16">
            <SummarySideBar
              submissionType={props.submissionType}
              submissions={submissions}
              currentUser={props.current_user}
              submissionName={submissionName}
              ndcDownloadPath={props.ndcDownloadPath}
              onError={(message) => addNotice({ kind: "error", message })}
            />
            <SubmissionSummary
              submissions={submissions}
              submissionType={props.submissionType}
              currentUser={props.current_user}
              onViewFile={handleViewFile}
              submissionName={submissionName}
            />
          </div>
        </div>
        <div style={{ display: activeTab === "submissions_data" ? "block" : "none" }}>
          <DataOutputProcessed
            submissions={submissions}
            ndcList={ndcList}
            requiredShape={submissions[0]?.columnNames || []}
            submissionType={props.submissionType}
            viewFileRequest={viewFileRequest}
          />
        </div>
        <div style={{ display: activeTab === "original_files" ? "block" : "none" }}>
          <DataOutputOriginal submissions={submissions} />
        </div>
      </>
    );
  };

  const onDragEnd = (result) => {
    const { destination } = result;

    if (!destination) return;

    const [dropZone, fieldName] = destination.droppableId.split("#");

    if (dropZone === "mapping") {
      // draggableId is `${fileId}#${columnName}`; strip the fileId prefix to get the column
      const columnName = result.draggableId.substring(result.draggableId.indexOf("#") + 1);

      // currently editing MappingGroup assigns it's local handler to the dropFnRef
      // call it here with dropped details
      dropFnRef.current?.(fieldName, columnName);
    }
  };

  return (
    <div style={{ display: "flex" }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="content__container">
          <DataSubmissionHeader
            submissionType={props.submissionType}
            ndcDownloadPath={props.ndcDownloadPath}
          />
          <NoticeStack notices={notices} dismissNotice={dismissNotice} disableClickaway />
          <TabBar tabs={tabs} activeTabId={activeTab} setActiveTab={setActiveTab} />
          {renderTabViews()}
        </div>
        <div className="draw__container">
          <DataInput
            organization={props.organization}
            ndcList={ndcList}
            reset={reset}
            submissionPath={props.submissionPath}
            fieldMappingGroups={fieldMappingGroups}
            attachments={submissions}
            removeSelectedFile={removeSelectedFile}
            handleFile={handleFile}
            submissionType={props.submissionType}
            submissionName={submissionName}
            setSubmissionName={setSubmissionName}
            expandFileHeaders={activeTab === "column_mappings"}
            setSelectedMapping={(attachment, mapping) =>
              handleSelectMapping(attachment, mapping.id)
            }
            setLeavePageLink={setLeavePageLink}
          />
        </div>
      </DragDropContext>
      {processingModalOpen && <ProcessingFileModal processingProgress={processingProgress} />}
      {leavePageLink && (
        <LeavePageModal leavePageLink={leavePageLink} onClose={() => setLeavePageLink(null)} />
      )}
    </div>
  );
};

export default DataSubmission;
